import { normalizeGroupBy, stringifyGroupBy } from './groupBy';

/**
 * @typedef {Object} DashboardItem
 * @property {string} [id]
 * @property {string} [client_name]
 * @property {string} [sku]
 * @property {string} [description]
 * @property {string} [variant]
 * @property {string} [size]
 * @property {string} [status]
 * @property {number} [quantity]
 */

/**
 * @typedef {Object} DashboardGroup
 * @property {string} key
 * @property {number} count
 * @property {number} total_quantity
 * @property {Record<string, number>} statuses
 * @property {Record<string, number>} sizes
 * @property {Record<string, number>} colors
 * @property {string[]} clients
 * @property {string[]} skus
 * @property {DashboardItem[]} items
 * @property {DashboardGroup[]} subgroups
 */

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toMap = (value) => {
  if (!value || typeof value !== 'object') return {};
  return Object.entries(value).reduce((acc, [key, quantity]) => {
    acc[String(key)] = toNumber(quantity);
    return acc;
  }, {});
};

const toStringList = (value) => {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item)).filter(Boolean);
};

const normalizeItem = (item) => ({
  id: item?.id || item?._id || '',
  _id: item?._id || item?.id || '',
  order_id: item?.order_id || item?.orderId || item?.idOrder || item?.order?._id || '',
  orderId: item?.orderId || item?.order_id || item?.idOrder || '',
  idOrder: item?.idOrder || item?.orderId || item?.order_id || '',
  client_name: item?.client_name || item?.client || '',
  sku: item?.sku || '',
  description: item?.description || '',
  variant: item?.variant || item?.color || '',
  size: item?.size || item?.talle || item?.talla || '',
  status: item?.status || '',
  quantity: toNumber(item?.quantity),
  raw: item
});

const normalizeGroup = (group) => ({
  key: String(group?.key || 'Sin clave'),
  count: toNumber(group?.count),
  total_quantity: toNumber(group?.total_quantity),
  statuses: toMap(group?.statuses),
  sizes: toMap(group?.sizes),
  colors: toMap(group?.colors),
  clients: toStringList(group?.clients),
  skus: toStringList(group?.skus),
  items: Array.isArray(group?.items) ? group.items.map(normalizeItem) : [],
  subgroups: Array.isArray(group?.subgroups) ? group.subgroups.map(normalizeGroup) : []
});

export const mapOrdersDashboardResponse = (response, fallbackGroupBy = ['client']) => {
  const groupFields = normalizeGroupBy(response?.group_by || fallbackGroupBy);
  return {
    group_by: stringifyGroupBy(groupFields),
    group_fields: groupFields,
    groups: Array.isArray(response?.groups) ? response.groups.map(normalizeGroup) : []
  };
};

export const flattenLeafGroups = (groups, level = 0, parentPath = []) => {
  const source = Array.isArray(groups) ? groups : [];
  return source.flatMap((group) => {
    const path = [...parentPath, group.key];
    const leaf = {
      ...group,
      level,
      path,
      id: path.join(' > ')
    };

    if (!group.subgroups?.length) return [leaf];
    return flattenLeafGroups(group.subgroups, level + 1, path);
  });
};

export const aggregateStatuses = (groups) => {
  const result = {};

  const walk = (nodes) => {
    (nodes || []).forEach((node) => {
      Object.entries(node.statuses || {}).forEach(([status, quantity]) => {
        result[status] = (result[status] || 0) + toNumber(quantity);
      });
      if (node.subgroups?.length) walk(node.subgroups);
    });
  };

  walk(groups);
  return result;
};
